import React, { BaseSyntheticEvent, useState, ReactNode } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { inlineTextIconMargin } from "@iventis/styles/src/atomic-rules";
import { Content } from "@iventis/translations/content/typed-content";
import { PermissionOwnerType } from "@iventis/domain-model/model/permissionOwnerType";
import axios, { CancelTokenSource } from "axios";
import { styled } from "@iventis/styles";
import { Box } from "@mui/material";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { permissionsApi } from "@iventis/api/src/api";
import { GroupOption, UserAndGroupSearchAggregate, UserOption, UsersGroupsResponse } from "./user-search.types";
import { getLabel } from "./permission-utilitites";

export const UserSearchComponent: React.FC<{
    onSelected: (selection: GroupOption | UserOption) => void;
    className?: string;
}> = ({ onSelected: emitSelection, className }) => {
    const translate = useIventisTranslate();

    const [options, setOptions] = useState<UserAndGroupSearchAggregate>([]);
    const [loading, setLoading] = useState(false);

    const [searchCancel, setSearchCancel] = useState<CancelTokenSource>();

    const search = async (query: string) => {
        optionSelected(undefined);
        if (query === "") {
            setOptions([]);
            return;
        }
        setLoading(true);

        const cancel = axios.CancelToken.source();
        if (searchCancel !== undefined) {
            searchCancel.cancel();
        }

        setSearchCancel(cancel);

        const response = await permissionsApi.get<UsersGroupsResponse>("/search", { params: { query }, cancelToken: cancel.token });

        const results: UserAndGroupSearchAggregate = [
            ...(response.data.users || []).map((user) => ({ type: PermissionOwnerType.User, value: user } as UserOption)),
            ...(response.data.groups || []).map((group) => ({ type: PermissionOwnerType.Group, value: group } as GroupOption)),
        ];

        setOptions(results);
        setLoading(false);
    };

    const optionSelected = (selection: string | GroupOption | UserOption) => {
        if (typeof selection === "string" || !selection) {
            emitSelection(undefined);
        } else {
            emitSelection(selection);
        }
    };

    return (
        <Autocomplete<GroupOption | UserOption, false, undefined, true>
            className={className}
            freeSolo
            filterOptions={(x) => x}
            options={options}
            loading={loading}
            loadingText={translate(Content.common["loading..."])}
            onChange={(e, selection) => optionSelected(selection)}
            getOptionLabel={(option) => getLabel(option as GroupOption | UserOption)}
            renderOption={(props, option: GroupOption | UserOption) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <Box component="li" {...props}>
                    {option.type === PermissionOwnerType.Group && (
                        <>
                            <StyledOptionIcon icon="users" />
                            {getLabel(option)}
                        </>
                    )}
                    {
                        (option.type === PermissionOwnerType.User && (
                            <>
                                <StyledOptionIcon icon="user-tie" />
                                {getLabel(option)}
                            </>
                        )) as ReactNode
                    }
                </Box>
            )}
            onInput={(event: BaseSyntheticEvent) => search(event.target.value)}
            renderInput={(params) => (
                <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    label={translate(Content.permissions.usersGroups.searchLabel)}
                    autoFocus
                    margin="normal"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "text", "aria-label": "textbox" }}
                    data-testid="search-users-or-groups"
                />
            )}
        />
    );
};

const StyledOptionIcon = styled(FontAwesomeIcon)`
    margin-right: ${inlineTextIconMargin};
`;
